<template>
  <div>
    <template v-if="!validatedSelectedOption">
      <!-- choose option -->
      <v-row
        dense
        justify="start"
        class="elevation-1 px-3 mx-1 py-4 my-5 grey lighten-2"
      >
        <v-col cols="12">
          <h3 class="pa-3">{{ $t('conflict.chooseYourFavoriteOption') }}:</h3>
        </v-col>
        <v-col cols="12">
          <wigo-chip
            v-for="(option, index) in availableOptions"
            :key="`option.id-${index}`"
            @click.native="selectOption(option)"
            :wigo="[option]"
            :outlined="isSameOption([option], selectedOption)"
            link
            :asGroup="asGroup"
          />
        </v-col>
      </v-row>
      <v-row
        justify="center"
        v-show="selectedOption"
      >
        <raison-button
          :button-label="$t('global.validate')"
          :button-function="validateSelectedOption"
        >
          <template v-slot:append>
            <v-icon right>mdi-arrow-right</v-icon>
          </template>
        </raison-button>
      </v-row>
    </template>
    <template v-else-if="validatedSelectedOption">
      <!-- Can you add elements to prefer x -->
      <v-row
        dense
        justify="start"
        class="elevation-1 px-3 mx-1 py-4 my-5 grey lighten-2"
      >
        <v-col cols="12">
          <h3 class="pa-3">{{ canYouAddElementToPrefer }}</h3>
        </v-col>
        <v-col cols="12">
          <element-chip
            v-if="beliefMode"
            :element="proposedOption[0]"
          />
          <wigo-chip
            v-show="proposedOption"
            :wigo="proposedOption"
            :asGroup="asGroup"
          />
        </v-col>
        <template v-if="canAddElementToPrefer">
          <v-col cols="12">
            <h3 class="pa-3">{{ $t('conflict.whichOnes') }}:</h3>
          </v-col>
          <v-col cols="12">
            <input-scenario-elements
              :available-elements="availableElements"
              :update-available-elements="updateElementList"
              create-new-scenario
              :validate-scenarii="validateNewElements"
            />
          </v-col>
        </template>
      </v-row>
      <!-- can add again to prefer yes/no -->
      <v-row
        v-show="!canAddElementToPrefer"
        no-gutters
        justify="center"
      >
        <v-btn
          @click="backtrack"
          color="primary"
          tile
          class="mx-1 px-7"
          height="50px"
        >
          <span v-show="noButtonLoader" class="rai-custom-loader__button">
            <v-icon dark>mdi-cached</v-icon>
          </span>
          {{ $t('global.no') }}
        </v-btn>
        <v-btn
          @click="canAddElementToPrefer = true"
          color="primary"
          tile
          class="mx-1 px-7"
          height="50px"
        >
          {{ $t('global.yes') }}
        </v-btn>
        <v-btn
          @click="discriminateWinningOptionsOfGroup"
          color="primary"
          tile
          class="mx-1 px-7"
          height="50px"
          v-show="asGroup && currentPreference.scenario.level > 2"
          :disabled="preferredOptions.length <= 1"
        >
          <span v-show="noButtonLoader" class="rai-custom-loader__button">
            <v-icon dark>mdi-cached</v-icon>
          </span>
          {{ $t('conflict.noButIWantToDiscriminateBetweenThePreferredOptions') }}
        </v-btn>
      </v-row>
    </template>
  </div>
</template>

<script>
import RaisonButton from '@/components/global/RaisonButton';
import WigoChip from '@/components/global/WigoChip';
import InputScenarioElements from '@/components/global/InputScenarioElements';
import believeService from '@/services/believes.js';
import scenarioService from '@/services/scenarios.js';
import elementService from '@/services/elements.js';

export default {
  name: 'Option1Workflow',

  components: { RaisonButton, WigoChip, InputScenarioElements },

  data () {
    return {
      selectedOption: [],
      validatedSelectedOption: false,
      canAddElementToPrefer: false,
      noButtonLoader: false,
      elementList: [],
      proposedOption: [],
      todo: [],
      currentPreference: null // used to challenge to challenge when it is possible to invert a choice
    };
  },

  props: {
    preferenceList: {
      type: Array,
      required: true,
    },
    currentScenario: {
      type: Object,
      required: true,
    },
    inputValidatedSelectedOption: {
      type: Boolean,
      required: false,
      default: false,
    },
    beliefMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    preferredOptions: {
      type: Array,
      required: false,
      default: () => []
    }
  },

  computed: {

    asGroup() {
      return !!(this.currentPreference?.groupId)
    },

    // return preferences related to the selected scenario
    scenarioPreferences() {
      const scenarioPreferences = this.preferenceList
        .filter(pref => pref.scenario.id === this.currentScenario.id);
      console.log('scenarioPreferences', scenarioPreferences, this.currentScenario.id);
      return scenarioPreferences;
    },

    availableOptions() {
      // Focus on option preferred for the selected scenario
      const options = this.scenarioPreferences
        .map(pref => pref[this.wigoProperty][0]) // never happens in group mode
        .filter((option, index, self) => self.findIndex(opt => opt.id === option.id) === index);
      return options;
    },

    canYouAddElementToPrefer() {
      let label = 'conflict.canYouAddElementToPrefer';
      if(this.asGroup) {
        label = 'conflict.canYouAddElementToPreferThisGroup';
      }
      else if(this.beliefMode) {
        label = 'conflict.canYouAddElementToPreferForBelief';
      }
      return this.$t(label);
    },

    wigoProperty() {
      return believeService.wigoProperty(this.beliefMode);
    },

    logoProperty() {
      return believeService.logoProperty(this.beliefMode);
    },

    availableElements () {
      if (this.currentScenario && this.currentScenario.elements && this.currentScenario.elements.length) {
        let currScenarioElementsId = this.currentScenario.elements.map(elt => elt.id);
        return this.elementList.filter(elt => !currScenarioElementsId.includes(elt.id));
      }
      return this.elementList;
    },
  },

  methods: {
    selectOption (option) {
      this.selectedOption = [option];
    },

    isSameOption(option1, option2) {
      return scenarioService.optionsKey(option1) !== scenarioService.optionsKey(option2);
    },

    // Never used with groups
    validateSelectedOption () {
      console.log('validateSelectedOption');

      // For each oj in GetScenarioEnabledOptions(S)
      console.log('GetScenarioEnabledOptions(S) = ', this.availableOptions);

      const selectedOptionKey = scenarioService.optionsKey(this.selectedOption);
      const newPreferences = this.availableOptions
        .filter(opt => opt.id !== selectedOptionKey)
        .map(opt => {
          // create new scenario
          // newS = copy of S
          // level(newS) = level(S)+1
          // newS- = S
          let scenarioCopy = {
            ...this.currentScenario,
            name: 'created from workflow 1',
            level: this.currentScenario.level + 1,
            id: '$ref1',
          };
          delete scenarioCopy.id;

          // find previous preference
          const previousPreference = this.scenarioPreferences
            .filter(pref => 
                 pref[this.wigoProperty][0].id === opt.id
              || pref[this.wigoProperty][0].id === selectedOptionKey)
            .map(pref => pref.id);

          let preference = {
            name: 'created in workflow 1',
            scenario: scenarioCopy,
            previousPreferences: previousPreference,
          };
          if(this.beliefMode) {
            preference = {
              ...preference,
              logoBelief: [{'id': opt.id}],
              wigoBelief: [{'id': selectedOptionKey}],
            };
          }
          else {
            preference = {
              ...preference,
              logo: [{'id': opt.id}],
              wigo: [{'id': selectedOptionKey}],
            };
          }
          // SP = SP union P = <{oi}, {oj}, newS>
          console.log('createPreferences - case 4.1')
          return preference;
        });

        scenarioService.createPreferences(this.$route.params.id, newPreferences)
          .then(response => {
            response.data.forEach(preference => {
              this.$emit('addPreferenceToList', preference);

              console.log('addTodo: case4.1a f_4.1_04(P)', preference);
              this.$emit('addTodo', {'preference': preference, 'todo': 'f_4.1_04(P)'});

              console.log('new scenario preference created', preference);
              this.validatedSelectedOption = true;
            });

            // oi = Form.options_to_select.selectedOption
            console.log('validateSelectedOption > preferedOption', this.selectedOption)
            this.$emit('preferedOption', this.selectedOption);

            this.backtrack();
          })
          .catch(err => {
            console.log('validateSelectedOption failed', err);
            this.$emit('raison-event', { type: 'error', messages: [err] });
          });

    },

    backtrack(addNextTodo=true) {
      this.$emit('addTodo', {'scenario': { ...this.currentScenario }, 'todo': 'extends false'});
      if(addNextTodo) {
        this.$emit('nextTodo');
      }
    },

    discriminateWinningOptionsOfGroup() {
      this.backtrack(false);
      // create minimal scenarios for the winning options
      const projectId = this.$route.params.id;
      scenarioService.discriminateWinningOptions(projectId, this.scenarioPreferences[0])
        .then(responses => {
          // and starts case 4.0 with this new scenario
          // We can get the scenario from any preferences
          // since all of them are linked to the same scenario
          const scenarioId = responses.data[0].scenario.id;
          this.$emit('addTodo', { scenarioId: scenarioId, todo: 'f_4_0_00'});
          this.$emit('nextTodo');
        })
    },

    validateNewElements (elements) {
      console.log('validate new elements !', elements);
      let newElementsSet = [...elements];
      newElementsSet.push(...this.currentScenario.elements);

      const scenarioRef1 = '$scenarioRef1';
      let newScenario = {
        id: scenarioRef1,
        extend: true,
        name: 'Comes from workflow1 4.1.2',
        level: this.currentScenario.level,
        elements: newElementsSet,
      };

      const preferenceRef1 = '$preferenceRef1';
      let preference = {
        ...this.currentPreference,
        id: preferenceRef1,
        name: 'created in workflow 1 - 4.1.2',
        scenario: newScenario,
        previousPreferences: [...this.currentPreference.previousPreferences], 
      };
      const newWigo = this.beliefMode ? this.currentPreference.logoBelief : this.currentPreference.logo;
      const newLogo = this.beliefMode ? this.currentPreference.wigoBelief : this.currentPreference.wigo;
      if(this.beliefMode) {
        preference = {
          ...preference,
          logoBelief: Object.assign([], newLogo.map(opt => {return {id: opt.id}})), // todo: convert
          wigoBelief: Object.assign([], newWigo.map(opt => {return {id: opt.id}})), // todo: convert
        };
      }
      else {
        preference = {
          ...preference,
          logo: Object.assign([], newLogo.map(opt => {return {id: opt.id}})), // todo: convert
          wigo: Object.assign([], newWigo.map(opt => {return {id: opt.id}})), // todo: convert
        };
      }

      // SP = SP union newP = < LoGO(P), WiGO(P), newS>

      let newSNL = {
        ...newScenario,
        extend: true,
        name: 'created in workflow 1 - 4.1.2 level+1',
        level: +this.currentScenario.level + 1,
        elements: [...newScenario.elements],
      };
      delete newSNL.id;

      // Sc = Sc union newSNL = < newE, level(scenario(P))+1, true, {scenario(P), newS}, {}>
      // SP = SP union newPNL = < LoGO(P), WiGO(P), newSNL>
      console.log('created NewSNL', newSNL);
      let preferenceNL = {
        ...preference,
        name: 'created in workflow 1 level+1',
        scenario: newSNL,
        previousPreferences: [this.currentPreference.id, preferenceRef1],
      };
      delete preferenceNL.id;

      console.log('right before creating preference', preferenceNL);
      // scenario(P)+= scenario(P)+ union newSNL
      scenarioService.createPreferences(this.$route.params.id, [preference, preferenceNL])
        .then(responses => {
          const createdPreferences = responses.data;
          createdPreferences.forEach(pref => {
            this.$emit('addPreferenceToList', pref);
          });
          const defaultPreference = responses.data[1];

          console.log('created new preferenceNL', defaultPreference);
          preferenceNL = defaultPreference;

          this.$emit('updateCurrentScenario', preferenceNL.scenario);
          this.$emit('updateCurrentPreference', preferenceNL);
          this.canAddElementToPrefer = false;

          // canExtend(newS) = false
          this.$emit('addTodo', {'scenario': responses.data[0].scenario, 'todo': 'extends false'}); // res[1] is supposed to be api returned convertedNewScenario

          console.log('addTodo: case4.1b f_4.1_04(P)', this.currentPreference);
          // f_4.1_04(P) reshow this screen after the next one returns in the case that there is yet another context for reversing the default preference with this option.
          this.$emit('addTodo', {'preference': this.currentPreference, 'todo': 'f_4.1_04(P)'});

          console.log("validateNewElements > preferedOption", newWigo);
          this.$emit('preferedOption', newWigo);
          this.proposedOption = newLogo;
          this.currentPreference = preferenceNL;

        })
        .catch(err => {
          console.log('createdPreferences 4.1.2 level+1 failed', err);
          this.$emit('raison-event', { type: 'error', messages: [err] });
        });

    },

    updateElementList (elements) {
      console.log('calling prop updateElementList', elements);
      this.elementList.splice(0, this.elementList.length, ...elements);
    },

  },

  mounted () {
    elementService.getElements(this.$route.params.id).then(res => {
      this.elementList = res.data;
    }).catch(err => {
      this.$emit('raison-event', { type: 'error', messages: [err] });
    })
    this.validatedSelectedOption = this.inputValidatedSelectedOption;
    this.proposedOption = this.availableOptions;
  },
};
</script>